import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Wrench } from 'lucide-react';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';

interface FixAchPaymentButtonProps {
  paymentIntentId: string;
  onSuccess?: () => void;
}

export function FixAchPaymentButton({ paymentIntentId, onSuccess }: FixAchPaymentButtonProps) {
  const [isFixing, setIsFixing] = useState(false);
  const { toast } = useToast();

  const handleFix = async () => {
    setIsFixing(true);
    try {
      const { data, error } = await invokeEdgeFunction('fix-ach-settlement', {
        body: { paymentIntentId },
        callerOperation: 'FixAchPaymentButton.fix',
      });

      if (error) throw error;

      const result = data as { success: boolean; poNumber?: string; error?: string };
      if (result.success) {
        toast({
          title: 'Payment Fixed',
          description: result.poNumber 
            ? `Payment updated and PO ${result.poNumber} created successfully`
            : 'Payment status updated successfully',
        });
        onSuccess?.();
      } else {
        throw new Error(result.error || 'Failed to fix payment');
      }
    } catch (error: any) {
      console.error('Fix failed:', error);
      toast({
        title: 'Fix Failed',
        description: error.message || 'Failed to fix ACH payment',
        variant: 'destructive',
      });
    } finally {
      setIsFixing(false);
    }
  };

  return (
    <Button
      onClick={handleFix}
      disabled={isFixing}
      variant="outline"
      size="sm"
    >
      {isFixing ? (
        <>
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Fixing...
        </>
      ) : (
        <>
          <Wrench className="mr-2 h-4 w-4" />
          Fix ACH Payment
        </>
      )}
    </Button>
  );
}
